import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Edit, Plus, Trash } from 'lucide-react';
import { Button } from '@edusense/shared-ui-kit';
import { ItemTypes } from './types';
import { DraggableLesson } from './DraggableLesson';
import * as S from './CurriculumTabContent.styles';
/**
 * 드래그 가능한 섹션 컴포넌트
 * 
 * 개별 섹션을 표시하고 드래그 앤 드롭, 편집, 삭제 기능을 제공
 * 섹션 내 강의 목록과 강의 추가 폼도 포함
 */ export const DraggableSection = ({ section, index, editingSectionId, editingSectionTitleCache, addingLessonToSectionId, newLessonTitleField, curriculumMachineSelectedLesson, editingLessonSectionId, editingLessonId, editingLessonTitleCache, onStartEditSection, onToggleAddLessonForm, onDeleteSection, onSubmitEditSection, onSubmitAddLesson, onSetEditingSectionTitle, onSetNewLessonTitle, onLessonSelect, onStartEditLesson, onDeleteLesson, onSubmitEditLesson, onSetEditingLessonTitle, onMoveLesson, onTogglePreview, send })=>{
    const ref = useRef(null);
    // 드래그 설정
    const [{ isDragging }, drag] = useDrag({
        type: ItemTypes.SECTION,
        item: {
            type: ItemTypes.SECTION,
            id: section.id,
            index
        },
        collect: (monitor)=>({
                isDragging: monitor.isDragging()
            })
    });
    // 드롭 설정
    const [, drop] = useDrop({
        accept: ItemTypes.SECTION,
        hover: (item, monitor)=>{
            if (!ref.current) return;
            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) return;
            // 머신에 이동 이벤트 전송
            send({
                type: 'MOVE_SECTION',
                dragIndex,
                hoverIndex
            });
            item.index = hoverIndex;
        }
    });
    drag(drop(ref));
    // 섹션 편집 시작 핸들러
    const handleEditClick = ()=>{
        onStartEditSection(section.id);
    };
    // 강의 추가 토글 핸들러
    const handleAddLessonClick = ()=>{
        onToggleAddLessonForm(section.id);
    };
    // 섹션 삭제 핸들러
    const handleDeleteClick = ()=>{
        onDeleteSection(section.id);
    };
    // 편집 제목 변경 핸들러
    const handleTitleChange = (e)=>{
        onSetEditingSectionTitle(e.target.value);
    };
    // 편집 폼 제출 핸들러
    const handleFormSubmit = (e)=>{
        onSubmitEditSection(e);
    };
    // 강의 추가 폼 제출 핸들러
    const handleLessonFormSubmit = (e)=>{
        onSubmitAddLesson(e, section.id);
    };
    // 새 강의 제목 변경 핸들러
    const handleNewLessonTitleChange = (e)=>{
        onSetNewLessonTitle(e.target.value);
    };
    // 강의 추가 취소 핸들러
    const handleCancelAddLesson = ()=>{
        onToggleAddLessonForm(section.id);
    };
    // 편집 중인지 확인
    const isEditing = editingSectionId === section.id;
    const isAddingLesson = addingLessonToSectionId === section.id;
    return /*#__PURE__*/ _jsxs(S.SectionItem, {
        ref: ref,
        $isDragging: isDragging,
        children: [
            /*#__PURE__*/ _jsxs(S.SectionHeader, {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        style: {
                            display: 'flex',
                            alignItems: 'center'
                        },
                        children: [
                            /*#__PURE__*/ _jsx(S.DragHandle, {
                                children: "≡"
                            }),
                            isEditing ? /*#__PURE__*/ _jsx("form", {
                                onSubmit: handleFormSubmit,
                                style: {
                                    flexGrow: 1
                                },
                                children: /*#__PURE__*/ _jsx(S.InputField, {
                                    value: editingSectionTitleCache,
                                    onChange: handleTitleChange,
                                    autoFocus: true,
                                    onBlur: onSubmitEditSection
                                })
                            }) : /*#__PURE__*/ _jsx(S.SectionTitle, {
                                children: section.title
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(S.SectionActions, {
                        children: [
                            /*#__PURE__*/ _jsx(S.ActionButton, {
                                onClick: handleEditClick,
                                title: "섹션 이름 수정",
                                children: /*#__PURE__*/ _jsx(Edit, {
                                    size: 16
                                })
                            }),
                            /*#__PURE__*/ _jsx(S.ActionButton, {
                                onClick: handleAddLessonClick,
                                title: "강의 추가",
                                children: /*#__PURE__*/ _jsx(Plus, {
                                    size: 16
                                })
                            }),
                            /*#__PURE__*/ _jsx(S.ActionButton, {
                                onClick: handleDeleteClick,
                                title: "섹션 삭제",
                                children: /*#__PURE__*/ _jsx(Trash, {
                                    size: 16
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(S.LessonList, {
                children: [
                    section.lessons.map((lesson, lessonIndex)=>/*#__PURE__*/ _jsx(DraggableLesson, {
                            lesson: lesson,
                            lessonIndex: lessonIndex,
                            sectionId: section.id,
                            isSelected: curriculumMachineSelectedLesson?.sectionId === section.id && curriculumMachineSelectedLesson?.lessonId === lesson.id,
                            editingLessonSectionId: editingLessonSectionId,
                            editingLessonId: editingLessonId,
                            editingLessonTitleCache: editingLessonTitleCache,
                            onMove: onMoveLesson,
                            onLessonSelect: onLessonSelect,
                            onStartEditLesson: onStartEditLesson,
                            onDeleteLesson: onDeleteLesson,
                            onSubmitEditLesson: onSubmitEditLesson,
                            onSetEditingLessonTitle: onSetEditingLessonTitle,
                            onTogglePreview: onTogglePreview,
                            send: send
                        }, lesson.id)),
                    isAddingLesson && /*#__PURE__*/ _jsxs(S.AddItemForm, {
                        onSubmit: handleLessonFormSubmit,
                        children: [
                            /*#__PURE__*/ _jsx(S.InputField, {
                                value: newLessonTitleField,
                                onChange: handleNewLessonTitleChange,
                                placeholder: "새 강의 제목",
                                autoFocus: true
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                type: "submit",
                                variant: "primary",
                                size: "sm",
                                children: "추가"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                type: "button",
                                variant: "outline",
                                size: "sm",
                                onClick: handleCancelAddLesson,
                                children: "취소"
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
